'use client';

import React, { useRef, useEffect, useState } from 'react';

import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { ELEMENT_IDS } from '../../constants/elementIds';
import { AppBadges } from '../AppBadge/AppBadges';
import { SelectLocation } from '../SelectLocation/index';
import { TranscendCookieBanner } from '../TranscendCookieBanner';

import { FooterAccordian } from './FooterAccordian';
import { FooterLinkItem } from './FooterLinkItem';
import { SocialBadges } from './SocialBadges';
import styles from './styles.module.css';

import { DownloadAppQRCode } from '@/components/DownloadAppQRCode';
import { HELP_ARTICLES } from '@/constants/helpUrls';
import { useConsentReconciliation } from '@/modules/consent/hooks/useConsentReconciliation';
import { isInHiddenConsentRegime } from '@/modules/consent/utils/isInHiddenConsentRegime';
import { FooterLowerConfig } from '@/modules/footer/constants';
import { getFooterConfig } from '@/modules/footer/getFooterConfig';
import type { FooterColumn, FooterLink } from '@/modules/footer/types';
import { useDynamicSelectWidth } from '@/modules/footer/useDynamicSelectWidth';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { useHideFooter } from '@/modules/sharedLayout/useHideFooter';

type Props = {
  isInAppView: boolean;
};

export function Footer({ isInAppView }: Props) {
  const { location } = useCurrentLocation();
  const t = useTranslations('common');
  const isFooterHidden = useHideFooter(isInAppView);
  const locationSelectRef = useRef<HTMLDivElement | null>(null);
  const { width: locationWidth, calculateWidth: calculateLocationWidth } =
    useDynamicSelectWidth({
      ref: locationSelectRef,
    });
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [manualShowCookieBanner, setManualShowCookieBanner] = useState(false);

  const footerConfig = getFooterConfig(location);

  const { reconciledConsentData, transcend, airgap, storedConsent } =
    useConsentReconciliation(isFooterHidden);

  const showCookieButton = airgap && !isInHiddenConsentRegime(airgap);

  useEffect(() => {
    if (manualShowCookieBanner) {
      return;
    }
    if (reconciledConsentData && storedConsent) {
      if (transcend && airgap) {
        setShowCookieBanner(!airgap.getConsent().confirmed);
      }
    }
  }, [
    reconciledConsentData,
    storedConsent,
    transcend,
    airgap,
    manualShowCookieBanner,
  ]);

  function handleCookiesClick() {
    setShowCookieBanner(true);
    setManualShowCookieBanner(true);
  }

  if (isFooterHidden) {
    return null;
  }

  const ariaProps = {
    ...(showCookieBanner && { 'aria-controls': 'cookies-modal' }),
    'aria-haspopup': 'dialog' as const,
  };
  return (
    <>
      {showCookieBanner && (
        <TranscendCookieBanner
          setShowBanner={setShowCookieBanner}
          manualShowBanner={manualShowCookieBanner}
          setManualShowBanner={setManualShowCookieBanner}
        />
      )}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <footer data-testid="footerV2" id={ELEMENT_IDS.footer}>
        <div className={styles.footerUpperContainer}>
          <div className={styles.footerUpperContent}>
            {/* The div below contains the mobile layout. What's in it is repeated again
            in a div with className="footerColumnDesktopContainer" */}
            <div className={styles.footerColumnMobileContainer}>
              {footerConfig.map(({ title, children }: FooterColumn) => (
                <FooterAccordian key={title} title={t(title) || title}>
                  {children.map((link: FooterLink) => (
                    <FooterLinkItem key={link.text} link={link} />
                  ))}
                </FooterAccordian>
              ))}
              <FooterAccordian title={t('Nav.SiteInformation')}>
                {FooterLowerConfig.map((link) => (
                  <FooterLinkItem key={link.text} link={link} />
                ))}
                {showCookieButton ? (
                  <button
                    className={styles.footerCookieButton}
                    onClick={handleCookiesClick}
                    {...ariaProps}
                  >
                    <Caption variant="lg-regular" colour="base">
                      {' '}
                      {t('Nav.Cookies')}
                    </Caption>
                  </button>
                ) : (
                  <div className={styles.footerLinkWrapper}>
                    <a
                      className={styles.footerLink}
                      href={HELP_ARTICLES.PRIVACY_POLICY}
                      target="_blank"
                      rel="noopener"
                    >
                      <Caption variant="lg-regular" colour="base">
                        {t('Nav.Cookies')}
                      </Caption>
                    </a>
                  </div>
                )}
              </FooterAccordian>
            </div>
            {/* This wraps the desktop links and buttons. Everything in here is also in
            the div with className="footerColumnMobileContainer" */}
            <div className={styles.footerColumnDesktopContainer}>
              {footerConfig.map(({ title, children }: FooterColumn) => (
                <div className={styles.footerColumn} key={title}>
                  <Heading variant="sm" className={styles.footerColumnTitle}>
                    {t(title) || title}
                  </Heading>
                  {children.map((link: FooterLink) => (
                    <FooterLinkItem key={link.text} link={link} />
                  ))}
                </div>
              ))}
            </div>
            <div className={styles.footerAppDownloadDesktopContainer}>
              <div className={styles.footerColumn}>
                <Heading variant="sm" className={styles.footerColumnTitle}>
                  {t('Nav.GetTheDepopApp')}
                </Heading>
                <div className={styles.footerQRCodeContainer}>
                  <DownloadAppQRCode withDepopLogo placement="footer" />
                  <Caption
                    variant="lg-regular"
                    colour="base"
                    className={styles.footerQRCodeText}
                  >
                    {t('DownloadApp.Footer.QRBody')}
                  </Caption>
                </div>
                <div className={styles.footerAppDownloadSocialBadges}>
                  <SocialBadges />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.footerAppSocialRow}>
            <div className={styles.footerAppDownload}>
              <Heading variant="sm">{t('Nav.GetTheDepopApp')}</Heading>
              <Caption
                variant="lg-regular"
                colour="base"
                className={styles.footerAppDownloadText}
              >
                {t('DownloadApp.Footer.MobileBody')}
              </Caption>
              <AppBadges
                className={styles.footerAppBadges}
                trackingId="footer"
                showBoth={true}
              />
            </div>
            <div className={styles.footerAppSocialContainer}>
              <SocialBadges />
            </div>
          </div>
          <div className="be-ix-link-block" />
        </div>
        <div className={styles.footerLowerContainer}>
          <div className={styles.footerLowerContent}>
            <div className={styles.footerLocalisationContainer}>
              <SelectLocation
                className={styles.selectLocation}
                label={t('Nav.SelectLocation')}
                ref={locationSelectRef}
                onChange={calculateLocationWidth}
                style={{
                  '--width': locationWidth ? `${locationWidth + 64}px` : '100%',
                }}
              />
            </div>
            <div className={styles.footerLowerDesktopColumn}>
              {FooterLowerConfig.map((link: FooterLink) => (
                <FooterLinkItem key={link.text} link={link} />
              ))}
              {showCookieButton ? (
                <button
                  className={styles.footerCookieButton}
                  onClick={handleCookiesClick}
                  {...ariaProps}
                >
                  <Caption variant="lg-regular" colour="base">
                    {t('Nav.Cookies')}
                  </Caption>
                </button>
              ) : (
                <div className={styles.footerLinkWrapper}>
                  <a
                    className={styles.footerLink}
                    href={HELP_ARTICLES.PRIVACY_POLICY}
                    target="_blank"
                    rel="noopener"
                  >
                    <Caption variant="lg-regular" colour="base">
                      {t('Nav.Cookies')}
                    </Caption>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
